import React from 'react';
import { Menu, Sidebar, Divider, Icon } from 'semantic-ui-react';
import { NavLink } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { initSelectors } from '../../behavior/init';
import {
  AccessRequest,
  Activity,
  BatchUpdate,
  Buildings,
  ChemicalProduct,
  Inventory,
  JobManagement,
  RequiredLmsCourses,
  Action,
} from '@src/routes';
import {
  CREATE_MODE,
  DELETED_OVERVIEW_TYPE,
  ENTITY_OVERVIEW_TYPE,
  entityType,
} from '@src/constants';
import { commonActions } from '../../behavior/common/index';
import { compose } from 'redux';
import { useTranslation, withTranslation } from 'react-i18next';
import { localStorage } from '@src/store';
import useUser from '@behavior/init/getUserV2';
import styles from '../Activity/components/styles/Activity.module.scss';
import { Header } from 'semantic-ui-react';
import '../../style.css';

type Props = {
  sidebar: boolean;
};

type SideBarProps = {
  divider?: boolean;
  to?: string;
  key?: string;
  onClick?: () => void;
  exact?: boolean;
  icon?: any;
  text?: string;
  visible?: boolean;
  content?: string;
  title?: boolean;
};

const NavSidebar = ({ sidebar }: Props) => {
  const { user } = useUser();
  const { t } = useTranslation();
  const accessRequestAndRequiredLmsCoursesMenuOptions: SideBarProps[] = [
    {
      to: AccessRequest.route(),
      icon: 'hand paper outline',
      text: 'menu.access.request',
      visible: true,
    },
    {
      to: RequiredLmsCourses.route(),
      icon: 'exclamation triangle',
      text: 'menu.required.lms.courses',
      visible: initSelectors.isMissingLmsCourse(user),
    },
  ];

  const allMenuOptions: SideBarProps[] = [
    {
      to: '/',
      exact: true,
      icon: 'home',
      text: 'menu.home',
      visible: !initSelectors.isPublic(user),
      onClick: () => localStorage.removeItem('defaultFilteredListOverride'),
    },
    {
      divider: true,
      key: 'divider-1',
      visible: !initSelectors.isPublic(user),
    },
    {
      to: Inventory.children.inventoryOverview.route(ENTITY_OVERVIEW_TYPE),
      icon: 'icon-tube-list',
      text: 'menu.inventory.records',
      visible: !initSelectors.isPublic(user),
      onClick: () => localStorage.removeItem('defaultFilteredListOverride'),
    },
    {
      to: Inventory.children.inventoryCreate.route(CREATE_MODE),
      icon: 'icon-tube-plus',
      text: 'menu.new.inventory.record',
      visible: initSelectors.isInventoryOrChemProductCanCreate(user, entityType.inventory),
    },
    {
      to: Inventory.children.inventoryOverview.route(DELETED_OVERVIEW_TYPE),
      icon: 'icon-tube-clock',
      text: 'menu.deleted.inventory.records',
      visible:
        initSelectors.isAdmin(user) ||
        initSelectors.isHseDeveloper(user) ||
        initSelectors.isChemicalSafetyExpert(user) ||
        initSelectors.isEnvironmentalExpert(user),
      onClick: () => localStorage.removeItem('defaultFilteredListOverride'),
    },
    {
      divider: true,
      key: 'divider-2',
      visible: !initSelectors.isPublic(user),
    },
    {
      to: ChemicalProduct.children.chemicalProductOverview.route(ENTITY_OVERVIEW_TYPE),
      icon: 'icon-bond-list',
      text: 'menu.chemical.products',
      visible: !initSelectors.isPublic(user),
      onClick: () => localStorage.removeItem('defaultFilteredListOverride'),
    },
    {
      to: ChemicalProduct.children.chemicalProductCreate.route(CREATE_MODE),
      icon: 'icon-bond-plus',
      text: 'menu.inventories',
      visible: initSelectors.isInventoryOrChemProductCanCreate(user, entityType.product),
    },
    {
      divider: true,
      key: 'divider-3',
      visible: initSelectors.isAllowedToSeeBuildings(user),
    },

    {
      to: Buildings.children.search.route(),
      icon: 'building',
      text: 'menu.buildings',
      visible: initSelectors.isAllowedToSeeBuildings(user),
    },
    {
      to: BatchUpdate.route(),
      icon: 'columns',
      text: 'menu.batch.update',
      visible:
        initSelectors.isAdmin(user) ||
        initSelectors.isHseDeveloper(user) ||
        initSelectors.isChemicalSafetyExpert(user) ||
        initSelectors.isDSO(user) ||
        initSelectors.isEditor(user),
    },
    {
      divider: true,
      key: 'divider-4',
      visible: !initSelectors.isPublic(user),
    },
    {
      title: true,
      content: 'menu.title.explosion.risk.assessment',
      visible: true,
    },
    {
      to: Activity.children.activityCreate.route(CREATE_MODE),
      key: 'icon-fire-plus',
      text: 'menu.new.activity.record',
      visible: initSelectors.isActivityCanCreate(user, entityType.activity),
    },
    {
      to: Activity.children.activityOverview.route(),
      icon: 'fire',
      text: 'menu.activity.overview',
      visible: initSelectors.isCanViewActivity(user, entityType.activity),
    },
    {
      to: Action.children.allActions.route(),
      icon: 'tasks',
      text: 'menu.action.overview',
      visible: true,
    },
    {
      divider: true,
      key: 'divider-5',
      visible: initSelectors.isAdmin(user) || initSelectors.isHseDeveloper(user),
    },
    {
      to: JobManagement.route(),
      icon: 'cogs',
      text: 'menu.job.management',
      visible: initSelectors.isAdmin(user) || initSelectors.isHseDeveloper(user),
    },
    {
      divider: true,
      key: 'divider-6',
      visible: true,
    },
    ...accessRequestAndRequiredLmsCoursesMenuOptions,
  ];

  return (
    <Sidebar
      as={Menu}
      animation="slide along"
      icon={sidebar ? 'labeled' : true}
      inverted
      vertical
      visible
      borderless
      width={sidebar ? 'thin' : undefined}
      className={sidebar ? 'state-open' : 'state-icon-only'}
      id="app-main-sidebar"
    >
      {(initSelectors.isMissingLmsCourse(user)
        ? accessRequestAndRequiredLmsCoursesMenuOptions
        : allMenuOptions
      ).map(({ divider, to, key, onClick, exact, icon, text, visible, content, title }) => {
        if (visible !== undefined && !visible) return null;
        if (divider) return <Divider key={key} />;
        if (title) {
          if (!sidebar) return <></>;
          return <Header content={content && t(content)} className="navbar-title" size="small" />;
        }

        return (
          <Menu.Item
            {...(to
              ? {
                  to,
                  as: NavLink,
                }
              : {})}
            key={key || to}
            exact={exact ? true : undefined}
            onClick={onClick}
          >
            {key == 'icon-fire-plus' ? (
              <div>
                <defs>
                  {React.createElement(
                    'font',
                    { id: 'custom-icons', 'horiz-adv-x': '2048' },
                    React.createElement('font-face', {
                      'units-per-em': '2048',
                      ascent: '1920',
                      descent: '-128',
                    }),
                    React.createElement('missing-glyph', { 'horiz-adv-x': '2048' })
                  )}
                  <Icon.Group className={sidebar ? styles.fireIconGroup : ''}>
                    <Icon name="fire" aria-hidden />
                    <Icon
                      corner="bottom right"
                      name="plus"
                      aria-hidden
                      className={sidebar ? styles.fire_add_icon_sidebar : styles.fire_add_icon}
                    />
                  </Icon.Group>
                  {/* @ts-ignore */}
                </defs>
              </div>
            ) : (
              <Icon className={icon} />
            )}

            {sidebar && text ? t(text) : null}
          </Menu.Item>
        );
      })}
    </Sidebar>
  );
};

NavSidebar.propTypes = {
  sidebar: PropTypes.bool.isRequired,
};

const mapStateToProps = (state: any) => ({
  sidebar: state.common.sidebar,
});

const mapDispatchToProps = (dispatch: any) => ({
  onToggleSidebar: () => dispatch(commonActions.toggleSidebar()),
});

export default compose(withTranslation(), connect(mapStateToProps, mapDispatchToProps))(NavSidebar);
